import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import type { ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { transformPersonAddressToApi } from 'services/person/us/edit/personAddressService'
import type { PersonBaseBasicInformation } from 'services/person/international/edit/personBasicInfoService'

/**
 * Fill payload. With an `id` it fills with an existing person; without one it
 * carries the new person's name (inline create). `lastName` is the only
 * required field — it mirrors the backend `PersonCreateSerializer`.
 */
export type FillPersonPayload = Partial<PersonBaseBasicInformation> & { lastName: string }

export async function getExecutivePosition (positionId: number): Promise<HttpResponse<ExecutivePosition>> {
  return await handleHttpRequest(http.get(`/api/v1/us/executives/positions/${positionId}/`))
}

export async function createExecutivePosition (
  position: ExecutivePosition
): Promise<HttpResponse<ExecutivePosition>> {
  const { person, ...withoutPerson } = position
  const base = position.vacant === true ? withoutPerson : position
  const payload = base.address != null
    ? { ...base, address: transformPersonAddressToApi(base.address) }
    : base
  return await handleHttpRequest(http.post('/api/v1/us/executives/positions/', payload))
}

export async function updateExecutivePosition (
  positionId: number,
  position: ExecutivePosition
): Promise<HttpResponse<ExecutivePosition>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/executives/positions/${positionId}/`, position))
}

export async function retireExecutivePosition (
  positionId: number
): Promise<HttpResponse<ExecutivePosition>> {
  return await handleHttpRequest(http.delete(`/api/v1/us/executives/positions/${positionId}/`))
}

export async function fillExecutivePosition (
  positionId: number,
  person: FillPersonPayload
): Promise<HttpResponse<ExecutivePosition>> {
  return await handleHttpRequest(
    http.post(`/api/v1/us/executives/positions/${positionId}/fill/`, { person })
  )
}

export async function deleteVacantExecutivePosition (
  positionId: number
): Promise<HttpResponse<null>> {
  return await handleHttpRequest(
    http.delete(`/api/v1/us/executives/positions/${positionId}/permanently/`)
  )
}
